import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography, CircularProgress, Divider, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectCities } from '../../../redux/slices/citiesSlice';
import { selectAdmins } from '../../../redux/slices/adminsSlice';
import axios from '../../../resources/axios';
import moment from 'moment';

interface ForecastHour {
  time: string;
  temp: number;
  feels_like: number;
  weather: string;
  icon: string;
  wind: number;
}

interface WeatherData {
  temperature: number;
  wind: number;
  description: string;
  icon: string;
  fetchedAt: string;
  forecast?: Record<string, ForecastHour[]>;
}

const WeatherWidget = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Маппінг міст на посилання прогнозу на місяць
  const cityWeatherUrls: Record<string, string> = {
    'Івано-Франківськ': 'https://meteofor.com.ua/weather-ivano-frankivsk-4959/month/',
    'Вінниця': 'https://meteofor.com.ua/weather-vinnytsia-4962/month/',
    'Житомир': 'https://meteofor.com.ua/weather-zhytomyr-4943/month/',
    'Коломия': 'https://meteofor.com.ua/weather-kolomyya-4971/month/',
    'Калуш': 'https://meteofor.com.ua/weather-kalush-12062/month/'
  };

  const currentCity = useMemo(() => {
    return cities.items.find(item => item._id === adminInfo.currentCity);
  }, [adminInfo.currentCity, cities.items]);

  useEffect(() => {
    const fetchWeather = async () => {
      if (!currentCity) {
        setError(t('stats.cityNotSelected'));
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get('/weather');

        if (!response.data?.data?.weather) {
          setError(t('stats.weatherDataStale'));
          setLoading(false);
          return;
        }

        const weather = response.data.data.weather;
        
        setWeatherData({
          temperature: weather.temperature,
          wind: weather.wind,
          description: weather.description,
          icon: weather.icon,
          fetchedAt: weather.fetchedAt,
          forecast: weather.forecast || {}
        });
      } catch (err: any) {
        console.error('Weather API error:', err);
        
        if (err.response?.status === 404) {
          setError(t('stats.weatherEndpointNotFound'));
        } else if (err.response?.status === 401) {
          setError(t('stats.authError'));
        } else {
          setError(t('stats.weatherLoadError'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
  }, [currentCity]);

  if (!currentCity) {
    return null;
  }

  return (
    <>
    <Paper
      sx={{
        padding: '20px',
        border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF'
          }}
        >
          {t('stats.weatherTitle', { city: currentCity.cityName })}
        </Typography>
        {cityWeatherUrls[currentCity.cityName] && (
          <Button
            size="small"
            variant="outlined"
            onClick={() => window.open(cityWeatherUrls[currentCity.cityName], '_blank')}
            sx={{
              fontSize: '12px',
              padding: '4px 12px'
            }}
          >
            {t('stats.forecastMonth')}
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress size={40} />
        </Box>
      )}

      {error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && weatherData && (
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: 'row',
            flex: 1,
            gap: 2
          }}
        >
          {/* Поточна погода - ліва колонка */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.mode === 'light' 
                ? 'rgba(33, 150, 243, 0.08)' 
                : 'rgba(33, 150, 243, 0.15)',
              borderRadius: 2,
              padding: '16px',
              minWidth: 160,
              flex: '0 0 auto'
            }}
          >
            <Box
              component="img"
              src={weatherData.icon}
              alt={weatherData.description}
              sx={{
                width: 56,
                height: 56,
                filter: theme.palette.mode === 'dark' ? 'brightness(1.1)' : 'none'
              }}
            />
            <Typography
              sx={{
                fontSize: 36,
                fontWeight: 700,
                color: theme.palette.mode === 'light' ? '#1976D2' : '#42A5F5',
                mt: 0.5,
                lineHeight: 1
              }}
            >
              {Math.round(weatherData.temperature)}°C
            </Typography>
            <Typography
              sx={{
                fontSize: 12,
                color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
                textTransform: 'capitalize',
                textAlign: 'center',
                mt: 0.5,
                mb: 1
              }}
            >
              {weatherData.description}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
                textAlign: 'center'
              }}
            >
              {t('stats.wind', { speed: Math.round(weatherData.wind) })}
            </Typography>
          </Box>

          {/* Прогноз на 5 днів - права колонка */}
          {weatherData.forecast && Object.keys(weatherData.forecast).length > 0 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  fontSize: 11,
                  color: theme.palette.mode === 'light' ? '#757575' : '#B0B0B0',
                  mb: 0.5,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}
              >
                {t('stats.forecast5Days')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {Object.entries(weatherData.forecast)
                  .slice(0, 5)
                  .map(([date, hours]) => {
                    const noonData = hours.find(h => h.time === '12:00:00') || hours[Math.floor(hours.length / 2)];
                    const maxTemp = Math.max(...hours.map(h => h.temp));
                    const minTemp = Math.min(...hours.map(h => h.temp));
                    
                    return (
                      <Box
                        key={date}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '5px 8px',
                          borderRadius: 1,
                          backgroundColor: theme.palette.mode === 'light' 
                            ? 'rgba(0, 0, 0, 0.02)' 
                            : 'rgba(255, 255, 255, 0.05)',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'light' 
                              ? 'rgba(0, 0, 0, 0.04)' 
                              : 'rgba(255, 255, 255, 0.08)'
                          }
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: theme.palette.mode === 'light' ? '#424242' : '#E0E0E0',
                            minWidth: 45
                          }}
                        >
                          {moment(date).format('DD.MM')}
                        </Typography>
                        <Box
                          component="img"
                          src={noonData?.icon || ''}
                          alt={noonData?.weather || ''}
                          sx={{
                            width: 24,
                            height: 24,
                            filter: theme.palette.mode === 'dark' ? 'brightness(1.1)' : 'none'
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.75, minWidth: 65, justifyContent: 'flex-end' }}>
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: theme.palette.mode === 'light' ? '#1976D2' : '#42A5F5'
                            }}
                          >
                            {Math.round(maxTemp)}°
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 11,
                              color: theme.palette.mode === 'light' ? '#9E9E9E' : '#757575'
                            }}
                          >
                            {Math.round(minTemp)}°
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Paper>

    </>
  );
};

export default WeatherWidget;
